import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TopNav } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ShieldCheck, Zap, Building2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LabPlansSection, TaxFooter } from "@/components/pricing/PricingSections";

const ACCREDITATIONS = ["NABL", "CAP", "ISO 9001", "ICMR"];

export default function LabSignup() {
  const nav = useNavigate();
  const { signupLab } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    lab_name: "",
    contact_name: "",
    email: "",
    phone: "",
    password: "",
    city: "",
    website: "",
    daily_reports: "",
    accreditations: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (a) => setForm((f) => ({
    ...f,
    accreditations: f.accreditations.includes(a) ? f.accreditations.filter((x) => x !== a) : [...f.accreditations, a],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const payload = {
        ...form,
        phone: form.phone.replace(/[^0-9]/g, ""),
        daily_reports: form.daily_reports ? Number(form.daily_reports) : undefined,
      };
      await signupLab(payload);
      toast.success("Application received! Setting you up…");
      nav("/lab");
    } catch (err) {
      toast.error(apiError(err, "Signup failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* LEFT — pitch */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">For Diagnostic Labs</div>
            <h1 className="mt-2 font-heading font-bold text-4xl sm:text-5xl text-ink leading-[1.05]">
              Onboard your lab.<br /><span className="italic font-normal">Delight every patient.</span>
            </h1>
            <p className="mt-4 text-lg text-ink/70 max-w-lg">
              Sign up in under 2 minutes. Your dashboard is live immediately — a super admin then activates your subscription plan and monthly report allotment.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                { icon: Zap, t: "Go live in <24 hours", s: "No LIS change. No disruption to your existing workflow." },
                { icon: Sparkles, t: "AI Smart Reports in 30 seconds", s: "PDFs & images → interactive, audio-narrated, patient-friendly reports." },
                { icon: Building2, t: "Full white-label", s: "Your logo, colours, domain, UPI ID, WhatsApp — pixel-perfect." },
                { icon: ShieldCheck, t: "DPDPA-2023 compliant", s: "ISO 27001-certified infra in India. Reports never leave your control." },
              ].map(({ icon: Icon, t, s }, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-efgh-orange/10 text-efgh-orange grid place-items-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-ink text-sm">{t}</div>
                    <div className="text-xs text-ink/60">{s}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-4 rounded-2xl bg-ink text-white text-sm">
              <div className="font-heading font-semibold">30-day free trial · No credit card</div>
              <div className="mt-1 text-white/60 text-xs">Our team will call within 1 business day to confirm pricing and go-live date.</div>
            </div>
          </div>

          {/* RIGHT — form */}
          <Card className="p-6 sm:p-8 rounded-2xl border-border shadow-xl" data-testid="lab-signup-card">
            <h2 className="font-heading font-semibold text-xl text-ink">Create your lab account</h2>
            <p className="mt-1 text-sm text-ink/60">Fields marked * are required.</p>
            <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="lab-signup-form">
              <div className="sm:col-span-2">
                <Label>Lab name *</Label>
                <Input data-testid="signup-lab-name" required value={form.lab_name} onChange={(e) => set("lab_name", e.target.value)} placeholder="e.g. Shree Diagnostics" />
              </div>
              <div>
                <Label>Your name *</Label>
                <Input data-testid="signup-contact-name" required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Dr. Suresh Kumar" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input data-testid="signup-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" />
              </div>
              <div>
                <Label>Work email *</Label>
                <Input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@yourlab.com" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input data-testid="signup-password" type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="min 8 characters" />
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="signup-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Pune" />
              </div>
              <div>
                <Label>Website</Label>
                <Input data-testid="signup-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://yourlab.com" />
              </div>
              <div className="sm:col-span-2">
                <Label>Reports processed per day</Label>
                <Input data-testid="signup-daily-reports" type="number" min={0} value={form.daily_reports} onChange={(e) => set("daily_reports", e.target.value)} placeholder="e.g. 250" />
              </div>
              <div className="sm:col-span-2">
                <Label>Accreditations</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ACCREDITATIONS.map((a) => {
                    const on = form.accreditations.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        data-testid={`accred-${a}`}
                        onClick={() => toggle(a)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink/40"}`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sm:col-span-2 mt-2 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-ink/50 max-w-xs">By signing up you agree to our T&amp;C. Your subscription plan is activated by an EFGH admin after a quick verification call.</div>
                <Button
                  data-testid="lab-signup-submit"
                  type="submit"
                  disabled={busy}
                  className="h-11 px-6 bg-efgh-orange hover:bg-efgh-orange/90 text-white shadow-lg shadow-efgh-orange/25"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <>Create lab account <CheckCircle2 className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
              <div className="sm:col-span-2 text-center text-sm text-ink/60 mt-2">
                Already onboarded? <Link to="/login" className="text-brand font-medium">Sign in</Link>
              </div>
            </form>
          </Card>
        </div>
      </div>

      {/* Lab pricing — pulled live from Super Admin's Pricing Management config */}
      <div className="border-t border-border bg-muted/30" data-testid="lab-signup-pricing">
        <div className="max-w-6xl mx-auto">
          <LabPlansSection
            eyebrow="Lab subscription plans"
            heading="Plans that scale with your lab volume"
            ctaTo="#"
            ctaLabel="Sign up above"
          />
          <div className="px-6 pb-10"><TaxFooter /></div>
        </div>
      </div>
    </div>
  );
}
